import * as React from "react";
import { AlertColor, Grid } from "@mui/material";
import ComponentLoader from "../../../../components/common/componentLoader";
import { useGetCclEventByEventKeyQuery } from "../../../../services/cclTokenedSessionApi";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import SessionDetailsAccordian from "./details/sessionDetailsAccordion";
import SessionAssessmentSetupAccordion from "./assessmentSetup/sessionAssessmentsSetupAccordian";
import { SessionProgramManagersAccordion } from "./programManagers/sessionProgramManagersAccordian";
import SessionSetupAccordion from "./setup/sessionSetupAccordion";
import ProductSaleSetupAccordion from "./setup/productSaleSetupAccordion";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import { useGetPayrollBySessionIdQuery } from "../../../../services/cclTokenedPayrollApi";

// Session Types:
// 3 Custom
// 6 Open Enrollment
// 7 Product Sale
// 10 Coaching

interface SessionGeneralPanelProps {
    sessionId: string;
    isProductSale?: boolean;
}

const SessionGeneralPanel: React.FC<SessionGeneralPanelProps> = (props) => {
    const [sessionDetail, setSessionDetail] = React.useState<SessionDetailType | undefined>(
        undefined,
    );
    const [isDraft, setIsDraft] = React.useState<boolean>(false);
    const [isActive, setIsActive] = React.useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [hasAssessmentPlatform, setHasAssessmentPlatform] = React.useState<boolean>(false);

    const {
        data: sessionData,
        isLoading,
        isSuccess,
    } = useGetCclEventByEventKeyQuery(props.sessionId, {
        skip: !props.sessionId,
    });

    React.useEffect(() => {
        if (sessionData === undefined) {
            setSessionDetail(undefined);
            return;
        }

        let sd: SessionDetailType = JSON.parse(JSON.stringify(sessionData));
        sd.start = sd.start != null ? new Date(sd.start.toString().slice(0, -1)) : null;
        sd.end = sd.end != null ? new Date(sd.end.toString().slice(0, -1)) : null;
        setSessionDetail(sd);
        setIsDraft(sd.status === "Draft");
        setIsActive(sd.status === "Active");
        setHasAssessmentPlatform(sd.assessmentPlatformKey === 1 || sd.assessmentPlatformKey === 6);
    }, [sessionData]);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    if (isLoading) {
        return <ComponentLoader msg={""} />;
    }

    if (isSuccess && sessionDetail !== undefined) {
        return (
            <Grid container spacing={2} sx={{ height: "fit-content" }}>
                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                    />
                ) : null}
                <Grid item xs={12} md={6}>
                    <SessionDetailsAccordian
                        sessionDetail={sessionDetail}
                        showSnackbar={ShowSnackbarElement}
                    />
                </Grid>
                {!isDraft ? (
                    <Grid item xs={12} md={6}>
                        {props.isProductSale ? (
                            <ProductSaleSetupAccordion
                                esKey={sessionDetail.sessionKey}
                                showSnackbar={ShowSnackbarElement}
                            />
                        ) : (
                            <SessionSetupAccordion
                                esKey={sessionDetail.sessionKey}
                                showSnackbar={ShowSnackbarElement}
                            />
                        )}
                    </Grid>
                ) : null}
                {!isDraft ? (
                    <Grid item xs={12}>
                        <SessionAssessmentSetupAccordion
                            esKey={sessionDetail.sessionKey}
                            projectCode={sessionDetail.sessionCode}
                            hasAssessmentPlatform={hasAssessmentPlatform}
                            hasVenue={sessionDetail.hasVenue}
                            isProductSale={sessionDetail.typeKey === 7}
                            showSnackbar={ShowSnackbarElement}
                        />
                    </Grid>
                ) : null}
                {isActive &&
                (sessionDetail?.typeKey === 3 ||
                    sessionDetail?.typeKey === 10 ||
                    props.isProductSale === true) &&
                sessionDetail?.typeKey !== 7 ? (
                    <Grid item xs={12}>
                        <SessionProgramManagersAccordion
                            esKey={sessionDetail.sessionKey}
                            programCode={sessionDetail.sessionCode}
                            hasAssessmentPlatform={hasAssessmentPlatform}
                            hasVenue={sessionDetail.hasVenue}
                            showSnackbar={ShowSnackbarElement}
                        />
                    </Grid>
                ) : null}
            </Grid>
        );
    }
    return <span>No Session Data Loaded</span>;
};

export default SessionGeneralPanel;
